import React, {useContext, useEffect} from 'react';

import classNames from 'classnames';

import {Button, IButtonProps} from './../button';
import {Container} from './../container';
import {disableScroll, enableScroll, IDateTime} from '../helpers';
import {Icon} from '../icon/icon';
import {Emoticons, ITextFieldProps, TextArea} from './../forms';
import {IProgressProps, Progress} from '../progress';
import {isToolTipButton, TCircleSize} from '../button/button';
import ReactMarkdown from 'react-markdown';
import {Scroll} from '../scroll-fade';
import {copyToClipboard} from '../../../app/src/mentor/mentor-store';
import {StoreContext} from '../../../app/src/base/root-store';
import {Dropdown} from '../dropdown';
import {Spinner} from '../spinner';
import {Grid, GridColumn} from '../grid';

export interface IChatProps {
    className?: string;
    isLoading?: boolean;
    items?: TChatItem[];
    reply?: TReply;
    isMentorChat?: boolean;
}

export interface IChatMessageProps {
    element: 'message';
    message: string;
    time: IDateTime;
    results?: IProgressProps[];
    hasClickableUrl?: boolean;
    conversationId?: string;
    tooltip?: string;
}

export interface IChatAnswerProps {
    element: 'answer';
    text: string;
    time: IDateTime;
    color?: 'red' | 'green';
}

export interface IChatReplyFormProps {
    textField: ITextFieldProps;
    actions: IButtonProps[];
}

export interface IChatFiveOptionsReplyProps {
    hasFiveOptions: boolean;
    actions: IButtonProps[];
}

export type TChatItem = IChatAnswerProps | IChatMessageProps;
export type TReply = IButtonProps[] | IChatReplyFormProps | IChatFiveOptionsReplyProps;

const Chat = (props: IChatProps): JSX.Element => {

    const mentorStore = useContext(StoreContext).mentorStore;
    let prevTime: string | undefined;
    const className: string = classNames(
        'chat',
        props.className,
    );

    useEffect(() => {
        disableScroll();

        if (!document.body.classList.contains('hide-scroll')) {
            document.body.classList.add('hide-scroll');
        }

        return (): void => {
            enableScroll();
            document.body.classList.remove('hide-scroll');
        };
    }, []);

    const isChatReplyForm = (reply: TReply): reply is IChatReplyFormProps => (reply as IChatReplyFormProps).textField !== undefined;
    const isFiveOptionsReply = (reply: TReply): reply is IChatFiveOptionsReplyProps => (reply as IChatFiveOptionsReplyProps).hasFiveOptions;
    const isButtons = (reply: TReply): reply is IButtonProps[] => Array.isArray(reply);

    const makeUrlsClickable = (text: string): string => {
        const urlRegex = /https?:\/\/\S+/g;

        return text.replace(urlRegex, (url) => {
            if (text.includes(`<a href=`)) {
                return url;
            }

            return `<a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a>`;
        });
    };

    const renderChatContent: (type: 'loader' | 'dialog', data?: IChatMessageProps) => JSX.Element = (type, data) => {
        const containsHtmlLink = (message: string): boolean => {
            const urlRegex = /https?:\/\/\S+/g;

            return urlRegex.test(message);
        };

        return (
            <div className="chat__item-grid">
                <div className="chat__column">
                    <div className="chat__bubble">
                        <Icon
                            name={props.isMentorChat ? 'robot-happy' : 'message'}
                            className={props.isMentorChat ? 'chat__icon__mentor' : 'chat__icon'}
                        />
                    </div>
                </div>
                <div className="chat__column">
                    <div className="chat__box">
                        {data && type === 'dialog' ? (
                            <div className="text">
                                {containsHtmlLink(data.message) ? (
                                    <div
                                        className="chat__message"
                                        dangerouslySetInnerHTML={{
                                            __html: makeUrlsClickable(data.message),
                                        }}
                                    />
                                ) : props.isMentorChat ? (
                                    <div
                                        className={`chat__message ${
                                            data?.conversationId ? 'chat__message__mentor' : 'chat__message'
                                        }`}
                                    >
                                        <ReactMarkdown>{data.message}</ReactMarkdown>
                                        {data?.conversationId && (
                                            <div className="chat__actions">
                                                {renderButtonWithTooltip({
                                                    iconRight: 'mail-out',
                                                    tooltip: mentorStore.label('chat.actions.email'),
                                                    onClick: () =>
                                                        mentorStore.sendMentorAdviceToEmail(data?.conversationId),
                                                })}
                                                {renderButtonWithTooltip({
                                                    iconRight: 'layers',
                                                    tooltip: mentorStore.label('chat.actions.copy'),
                                                    onClick: () => copyToClipboard(data.message),
                                                })}
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <div className="chat__message">{data.message}</div>
                                )}
                                {data.results && (
                                    <Grid wrap={['xs']}>
                                        {data.results.map((el: IProgressProps, i: number) => (
                                            <GridColumn width={['xs', 'sm-6']} key={i}>
                                                <Progress {...el} />
                                            </GridColumn>
                                        ))}
                                    </Grid>
                                )}
                            </div>
                        ) : (
                            <div className="chat__loader" />
                        )}
                    </div>
                </div>
            </div>
        );
    };

    const renderButtonWithTooltip = (action: IButtonProps) => {
        if (isToolTipButton(action)) {
            if (action.isLoading) {
                return <Spinner />;
            } else {
                return (
                    <Dropdown
                        trigger={{
                            children: <Button {...action} style="link" className={'button__noFill__chat__action'} />,
                        }}
                    >
                        {action.tooltip}
                    </Dropdown>
                );
            }
        } else {
            return <Button {...action} style="link" className={'button__noFill__chat__action'} />;
        }
    };

    const renderAnswer: (data: IChatAnswerProps) => JSX.Element = (data: IChatAnswerProps) => {
        const answerClassName: string = classNames(
            'chat__answer',
            {
                [`chat__answer--color-${data.color}`]: !!data.color,
            },
        );

        return (
            <div className={answerClassName}>
                <div className="chat__answer-inner">
                    <Emoticons text={data.text}/>
                </div>
            </div>
        );
    };

    return (
        <div className={className}>
            <Scroll start="bottom">
                <Container>
                    <div className={`chat__inner${props.isMentorChat ? ' chat__inner__wide' : ''}`}>
                        {props.items && props.items.map((item: TChatItem, i: number) => {
                            const timeClassName: string = classNames(
                                'chat__time',
                                {
                                    'is-hidden': prevTime === item.time.iso,
                                },
                            );

                            prevTime = item.time.iso;

                            return (
                                <div className="chat__item" key={i}>
                                    <div className={timeClassName}>
                                        <time className="text-small" dateTime={item.time.iso}>{item.time.value}</time>
                                    </div>
                                    {item.element === 'message' && renderChatContent('dialog', item)}
                                    {item.element === 'answer' && renderAnswer(item)}
                                </div>
                            );
                        })}
                        {props.isLoading && (
                            <div className="chat__item">
                                {renderChatContent('loader')}
                            </div>
                        )}
                        {props.reply &&(
                            <>
                                {isChatReplyForm(props.reply) && renderChatReplyForm(props.reply, props.isMentorChat)}
                                {isButtons(props.reply) && renderChatReplyButtons(props.reply)}
                                {isFiveOptionsReply(props.reply) && renderChatFiveOptionsReplyButtons(props.reply)}
                            </>
                        )}
                    </div>
                </Container>
            </Scroll>
        </div>
    );
};

const renderChatReplyForm = (reply: IChatReplyFormProps, isMentorChat?: boolean): JSX.Element => <div className="chat__reply-form">
    <TextArea {...reply.textField} hideLabel="no-break"/>
    <div className={`chat__replies${isMentorChat === true ? ' chat_wide' : ''}`}>
        {reply.actions.map((action: IButtonProps, i: number) => (
            <div className="chat__reply" key={i}>
                <Button {...action} />
            </div>
        ))}
    </div>
</div>;

const renderChatReplyButtons = (reply: IButtonProps[]): JSX.Element => <div className="chat__replies">
    {reply.map((el: IButtonProps, i: number) => (
        <div className={`chat__reply ${reply.length === 2 ? 'chat__reply_binary' : 'chat__reply_multiple'}`}
            key={i}>
            <Button {...el} />
        </div>
    ))}
</div>;

const renderChatFiveOptionsReplyButtons = (reply: IChatFiveOptionsReplyProps): JSX.Element => <div className="chat__reply-form five-options">
    {reply.actions.map((el: IButtonProps, i: number) => (
        <div className={'chat__reply-five-options'}
            key={i}>
            <Button {...el} style={'circle'} size={i+1 as TCircleSize}/>
        </div>
    ))}
</div>;

export default Chat;
